import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  Snackbar
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { dcApi } from '../services/dc.api'
import { DCParserConfig } from '../types/dc.types'

export default function DCSettingsPage() {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editConfig, setEditConfig] = useState<DCParserConfig | null>(null)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  })

  // Form state
  const [formData, setFormData] = useState({
    distributor_pattern: '',
    parser_type: 'table',
    header_row: 1,
    column_mappings: '{}',
    extraction_rules: '{}',
    is_active: true
  })

  // Fetch parser configs
  const { data: configs = [], isLoading, error } = useQuery<DCParserConfig[]>({
    queryKey: ['parser-configs'],
    queryFn: () => dcApi.getParserConfigs()
  })

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data: Partial<DCParserConfig>): Promise<void> => {
      if (editConfig) {
        await dcApi.updateParserConfig(editConfig.id!, data)
      } else {
        await dcApi.createParserConfig(data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parser-configs'] })
      setDialogOpen(false)
      resetForm()
      setSnackbar({
        open: true,
        message: editConfig ? 'Config updated successfully' : 'Config created successfully',
        severity: 'success'
      })
    },
    onError: (err: Error) => {
      setSnackbar({
        open: true,
        message: `Error: ${err.message}`,
        severity: 'error'
      })
    }
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: dcApi.deleteParserConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parser-configs'] })
      setSnackbar({
        open: true,
        message: 'Config deleted successfully',
        severity: 'success'
      })
    },
    onError: (err: Error) => {
      setSnackbar({
        open: true,
        message: `Error: ${err.message}`,
        severity: 'error'
      })
    }
  })

  const resetForm = () => {
    setFormData({
      distributor_pattern: '',
      parser_type: 'table',
      header_row: 1,
      column_mappings: '{}',
      extraction_rules: '{}',
      is_active: true
    })
    setEditConfig(null)
  }

  const handleEdit = (config: DCParserConfig) => {
    setEditConfig(config)
    setFormData({
      distributor_pattern: config.distributor_pattern || '',
      parser_type: config.parser_type || 'table',
      header_row: config.header_row || 1,
      column_mappings: JSON.stringify(config.column_mappings || {}, null, 2),
      extraction_rules: JSON.stringify(config.extraction_rules || {}, null, 2),
      is_active: config.is_active !== false
    })
    setDialogOpen(true)
  }

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this parser config?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleSave = () => {
    try {
      const data: Partial<DCParserConfig> = {
        distributor_pattern: formData.distributor_pattern,
        parser_type: formData.parser_type as DCParserConfig['parser_type'],
        header_row: formData.header_row,
        column_mappings: JSON.parse(formData.column_mappings),
        extraction_rules: JSON.parse(formData.extraction_rules),
        is_active: formData.is_active
      }
      saveMutation.mutate(data)
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Invalid JSON in column mappings or extraction rules',
        severity: 'error'
      })
    }
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load parser configurations.
      </Alert>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Settings
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm()
            setDialogOpen(true)
          }}
        >
          Add Parser Config
        </Button>
      </Box>

      {/* Parser Configurations */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Parser Configurations
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Configure how different distributor price lists should be parsed.
          AI-generated configs are created automatically when parsing unknown formats.
        </Typography>

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : configs.length === 0 ? (
          <Alert severity="info">
            No parser configurations yet. They will be created automatically when you upload files.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Pattern</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Success Rate</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.map((config: DCParserConfig) => (
                  <TableRow key={config.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {config.distributor_pattern?.substring(0, 30)}
                        {(config.distributor_pattern?.length || 0) > 30 ? '...' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={config.parser_type}
                        color={config.parser_type === 'table' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={`${(config.success_rate || 0).toFixed(0)}%`}
                        color={
                          (config.success_rate || 0) >= 80
                            ? 'success'
                            : (config.success_rate || 0) >= 50
                            ? 'warning'
                            : 'error'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={config.created_by || 'system'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={config.is_active ? 'Active' : 'Inactive'}
                        color={config.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEdit(config)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(config.id!)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editConfig ? 'Edit Parser Config' : 'Add Parser Config'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Distributor Pattern (Regex)"
                value={formData.distributor_pattern}
                onChange={(e) => setFormData(prev => ({ ...prev, distributor_pattern: e.target.value }))}
                helperText="Regular expression to match distributor name in documents"
                placeholder="e.g., ALPHA.*PHARMA"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Parser Type"
                value={formData.parser_type}
                onChange={(e) => setFormData(prev => ({ ...prev, parser_type: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="table">Table</option>
                <option value="text">Text</option>
                <option value="custom">Custom</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Header Row"
                value={formData.header_row}
                onChange={(e) => setFormData(prev => ({ ...prev, header_row: parseInt(e.target.value) || 1 }))}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Column Mappings (JSON)"
                value={formData.column_mappings}
                onChange={(e) => setFormData(prev => ({ ...prev, column_mappings: e.target.value }))}
                helperText='e.g., {"0": "product_name", "1": "discount_text"}'
                sx={{ '& textarea': { fontFamily: 'monospace' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Extraction Rules (JSON)"
                value={formData.extraction_rules}
                onChange={(e) => setFormData(prev => ({ ...prev, extraction_rules: e.target.value }))}
                helperText="Regex patterns for extracting values"
                sx={{ '& textarea': { fontFamily: 'monospace' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
